/**
 * The presentation contract for every error the console shows.
 *
 * Call sites raise errors; they do not decide how those errors look. `toPresentedError`
 * is the single mapping from a backend or client error to a `PresentedError`, and
 * `<arp-error-block>` is the single renderer. Adding a new error case means adding a
 * matcher here, not styling something new at the call site.
 */

import {
  LucideBan,
  LucideCheck,
  LucideCircleAlert,
  LucideCircleQuestionMark,
  LucideClock,
  LucideCloudAlert,
  LucideCopy,
  LucideInfo,
  LucideLock,
  LucideLockOpen,
  LucideLogIn,
  LucideMail,
  LucideMessageSquareOff,
  LucideRefreshCw,
  LucideX,
  type LucideIcon,
} from '@lucide/angular'

/** Contact link used when an error has no remedy the user can act on. */
export const SUPPORT_CONTACT_URL = 'https://arpio.io/contact-us/'
export const SUPPORT_EMAIL_URL = 'mailto:support@arpio.io'

/**
 * Red failed · gold gated or throttled · blue informational · gray system state, no fault.
 * Teal is the primary-action color and is never a severity.
 */
export type ErrorSeverity = 'failure' | 'gating' | 'info' | 'neutral'

/**
 * The surface that owns the message, smallest first. The error renders in the smallest
 * surface that owns the failure; `global` means the cause is no longer on screen.
 */
export type ErrorTier = 'field' | 'surface' | 'object' | 'page' | 'global'

/** How long the block stays up. Auto-clearing is only for conditions the system resolves. */
export type ErrorPersistence =
  | 'until-edited'
  | 'until-closed'
  | 'until-state-change'
  | 'sticky'
  | 'self-clearing'
  | { ms: number }

/**
 * A remedy. `route`/`href` render as links; anything else emits `id` from the block so the
 * host can run it. `copy-details` is handled by the block itself.
 */
export interface ErrorAction {
  id: string
  label: string
  /**
   * Seconds remaining on a throttle. The renderer ticks this down on the control itself, so
   * the user watches the wait expire rather than re-reading a stale number. Implies disabled.
   */
  countdownSeconds?: number
  /** `quiet` is the neutral outline used for Copy Details, Dismiss and secondary remedies. */
  kind?: 'severity' | 'quiet'
  route?: string
  href?: string
  disabled?: boolean
}

/** The meta line. Never dropped, even when the human copy above it is a rewrite. */
export interface ErrorReference {
  code?: number
  causeCode?: string
  /** Populated once arpio-api returns it. */
  requestId?: string
  at: Date
  /** The untouched backend string. */
  raw?: string
}

/** One row of a structured failure — a per-application or per-issue line. */
export interface PresentedErrorItem {
  id?: string
  kind: 'application' | 'issue'
  reason: string
  causeCode?: string
  remedy?: { label: string; route?: string }
  /** True for rows that succeeded, so partial outcomes can list them last. */
  ok?: boolean
}

export interface PresentedError {
  severity: ErrorSeverity
  tier: ErrorTier
  /** What failed, in the user's words. No HTTP codes, no API nouns. */
  title: string
  /** The consequence, plus what is still safe. */
  detail: string
  /** Lucide glyph, rendered through `<svg [lucideIcon]>` since it comes from data. */
  glyph: LucideIcon
  actions: ErrorAction[]
  reference: ErrorReference
  items?: PresentedErrorItem[]
  persistence: ErrorPersistence
  /**
   * Why the owning surface's primary action cannot be retried as-is. A dialog disables its
   * footer CTA and shows this beside it, so the error is never the user's first discovery
   * that they are blocked. Absent means retrying is worth a try.
   */
  blockedReason?: string

  /**
   * `field` tier only: the logical control this belongs to. A form host claims the error by
   * matching this, which is how a backend validation failure lands on the same input as the
   * local validator instead of in a corner toast.
   */
  field?: ErrorField
}

/**
 * Logical control names, not template ids — a host maps these onto its own controls. Kept as
 * a union so a matcher can't invent a field no host listens for.
 */
export type ErrorField = 'role-name' | 'saml-metadata'

/**
 * Where the error was raised. The same API error routes to a different tier depending on
 * which surface is on screen to own it.
 */
export interface ErrorContext {
  dialogId?: string
  /**
   * The panel or row that owns the object the failure is about. A submit from a dialog names
   * both: the dialog declines an object-tier error on tier, and the panel behind it claims it.
   */
  objectId?: string
  fieldName?: string
  applicationId?: string
  jobId?: string
  /**
   * Set by `UiService` when a named surface is currently mounted. Call sites do not set this
   * themselves — they name their surface and let the registry confirm it.
   */
  anchored?: boolean
  /**
   * Runs the remedy when the user clicks it. Needed for a failure that falls through to a
   * toast: the shell has no way back to the component, and a `PresentedError` holds data only.
   * Stripped before the error is built — it steers nothing about how the error reads.
   */
  onAction?: (action: ErrorAction) => void
}

/** The floor every renderer falls back to, so an error never renders with no button at all. */
export const COPY_DETAILS: ErrorAction = { id: 'copy-details', label: 'Copy Details', kind: 'quiet' }
const CONTACT_SUPPORT: ErrorAction = {
  id: 'contact-support',
  label: 'Contact Support',
  kind: 'quiet',
  href: SUPPORT_CONTACT_URL,
}

/** Every glyph here must also be registered in `LucideIconsModule`. */
export const ERROR_GLYPHS = {
  failure: LucideCircleAlert,
  clock: LucideClock,
  info: LucideInfo,
  question: LucideCircleQuestionMark,
  connectivity: LucideCloudAlert,
  disabled: LucideBan,
  lock: LucideLock,
  unlock: LucideLockOpen,
  session: LucideLogIn,
  envelope: LucideMail,
  retry: LucideRefreshCw,
  chatOff: LucideMessageSquareOff,
  copy: LucideCopy,
  check: LucideCheck,
  dismiss: LucideX,
} as const

const DEFAULT_GLYPHS: Record<ErrorSeverity, LucideIcon> = {
  failure: ERROR_GLYPHS.failure,
  gating: ERROR_GLYPHS.clock,
  info: ERROR_GLYPHS.info,
  neutral: ERROR_GLYPHS.question,
}

/**
 * True when the reference carries more than a timestamp. A locally-authored form error has
 * nothing to correlate, so rendering a bare ISO string would be noise.
 */
export function hasReferenceDetail(reference: ErrorReference): boolean {
  return Boolean(reference.code !== undefined || reference.raw || reference.causeCode || reference.requestId)
}

/** Renders the reference line, and the payload behind Copy Details. */
export function formatErrorReference(reference: ErrorReference): string {
  const parts: string[] = []
  if (reference.code !== undefined && reference.code !== null) parts.push(String(reference.code))
  if (reference.raw) parts.push(reference.raw)
  if (reference.causeCode && !reference.raw?.includes(reference.causeCode)) {
    parts.push(`Cause: ${reference.causeCode}`)
  }
  if (reference.requestId) parts.push(reference.requestId)
  parts.push(reference.at.toISOString())
  return parts.join(' · ')
}

/**
 * Pulls `Cause: SOME_CODE` off the end of a backend message. The backend appends these to
 * the human sentence rather than returning them as a field.
 */
function extractCauseCode(raw: string | undefined): string | undefined {
  const match = raw?.match(/Cause:\s*([A-Za-z0-9_]+)/)
  return match ? match[1] : undefined
}

/**
 * Turns a backend cause code into a sentence. The keys are `ResumeRecoveryUnavailableCauseDto`
 * values, which serialize camelCase, so they are matched verbatim. Codes not listed here fall
 * through to the generic copy with the code still visible in the reference line.
 */
const CAUSE_CODE_COPY: Record<string, string> = {
  compatibility:
    'The recovery point or the application configuration changed after that recovery ran, so it cannot pick up where it left off. Run a new recovery instead.',
  targetPlanningFailed:
    'That recovery never got far enough to plan its target, so there is no progress to resume. Run a new recovery instead.',
}

/**
 * Werkzeug's stock 409 description, which arpio-api returns whenever it raises a bare
 * `Conflict()` — the optimistic-concurrency case where refreshing genuinely helps. There is no
 * error-code field on the wire (`json_error` sends `{message, authenticateUrl}` only), so the
 * presence of a message the backend authored is the only signal that a 409 is not staleness.
 */
const STOCK_CONFLICT_DESCRIPTION = 'a conflict happened while processing the request'

/** Duplicate-resource 409s across the API: a subject email, an application name, an account. */
const DUPLICATE_CONFLICT = /already exists|already in use/i

interface RawError {
  responseCode?: number
  body?: { message?: string }
  message?: string
  detailedMessage?: string
  /** Legacy announceError flag: pin the toast instead of auto-dismissing. */
  sticky?: boolean
}

/**
 * Maps an error to its presentation. Pure and synchronous so it can be unit-tested against
 * the real payloads in the test page's `toastGroups`.
 */
export function toPresentedError(
  error: unknown,
  context: ErrorContext = {},
  fallbackTitle = 'Something went wrong'
): PresentedError {
  const at = new Date()
  const err = (typeof error === 'object' && error !== null ? error : {}) as RawError
  const code = err.responseCode
  const clientMessage = err.detailedMessage || err.message
  const raw = err.body?.message || clientMessage
  const causeCode = extractCauseCode(raw)
  const reference: ErrorReference = { code, causeCode, at, raw }

  const build = (partial: Omit<PresentedError, 'reference' | 'glyph'> & { glyph?: LucideIcon }): PresentedError => ({
    ...partial,
    glyph: partial.glyph ?? DEFAULT_GLYPHS[partial.severity],
    reference,
  })

  // Substring-matched handlers first — a specific remedy always beats the code default.
  if (raw) {
    const matched = matchByMessage(raw, code, causeCode, context, build)
    if (matched) return matched
  }

  if (code !== undefined && code !== null) {
    return byResponseCode(code, raw, context, build)
  }

  // Raised in the client rather than by the API (announceError callers). Raw HttpErrors put
  // an http-framework string in `message`, so anything over 60 characters is body copy.
  if (clientMessage) {
    const usableTitle = err.message && err.message.length <= 60 && err.message !== clientMessage
    return build({
      severity: 'failure',
      tier: anchoredTier(context, 'surface'),
      title: usableTitle ? err.message : fallbackTitle,
      detail: clientMessage,
      actions: [COPY_DETAILS],
      persistence: err.sticky ? 'sticky' : { ms: 5000 },
    })
  }

  return build({
    severity: 'failure',
    tier: 'global',
    title: fallbackTitle,
    detail: 'An unexpected error occurred. Try again, or contact support if it repeats.',
    actions: [COPY_DETAILS, CONTACT_SUPPORT],
    persistence: 'sticky',
  })
}

/**
 * A locally-authored, form-scoped error — a failed sign-in, a rejected registration. There is
 * no backend payload to preserve and no remedy button to offer: the remedy is to correct the
 * form, so it carries no actions and no reference line.
 */
export function toFormError(title: string, detail?: string): PresentedError {
  return {
    severity: 'failure',
    tier: 'surface',
    title,
    detail: detail ?? '',
    glyph: DEFAULT_GLYPHS.failure,
    actions: [],
    reference: { at: new Date() },
    persistence: 'until-edited',
  }
}

type Build = (partial: Omit<PresentedError, 'reference' | 'glyph'> & { glyph?: LucideIcon }) => PresentedError

function matchByMessage(
  raw: string,
  code: number | undefined,
  causeCode: string | undefined,
  context: ErrorContext,
  build: Build
): PresentedError | null {
  const lower = raw.toLowerCase()

  if (lower.includes('role name already exists')) {
    return build({
      severity: 'failure',
      tier: 'field',
      title: 'A role with this name already exists',
      detail: 'Choose a different name.',
      actions: [],
      persistence: 'until-edited',
      field: 'role-name',
    })
  }

  if (lower.includes('saml metadata')) {
    // Branches follow the four distinct SamlMetadataError messages, matched on the phrase unique
    // to each. `non-expired` has to be tested before `entitydescriptor` because the real message
    // contains both.
    const keepsConfig = 'SSO keeps using your current configuration until you save a valid document.'
    let detail: string
    if (lower.includes('could not be parsed')) {
      detail = `Copy the complete XML metadata from your identity provider, including all opening and closing tags. ${keepsConfig}`
    } else if (lower.includes('non-expired')) {
      detail = `The document has no unexpired EntityDescriptor with an IDPSSODescriptor. Download fresh metadata from your identity provider and try again. ${keepsConfig}`
    } else if (lower.includes('more than one idpssodescriptor')) {
      detail = `The document describes several identity providers. Export metadata for just the one you want to use, so it contains a single EntityDescriptor with a single IDPSSODescriptor. ${keepsConfig}`
    } else if (lower.includes('http-post binding')) {
      detail = `The identity provider must offer exactly one HTTP-POST binding. Enable HTTP-POST for this application in your identity provider and export the metadata again. ${keepsConfig}`
    } else {
      detail = `Copy the complete, current metadata XML from your identity provider. ${keepsConfig}`
    }
    return build({
      severity: 'failure',
      tier: 'field',
      title: 'The SAML metadata you provided is not valid',
      detail,
      actions: [],
      persistence: 'until-edited',
      field: 'saml-metadata',
    })
  }

  // Scoped to the invite gate: the same sentence prefixes a billing-notification gate whose
  // tail differs, and this copy names inviting users specifically.
  if (lower.includes('verify your email address') && lower.includes('add other users')) {
    return build({
      severity: 'gating',
      tier: 'surface',
      title: 'Verify your email before inviting users',
      detail:
        'You must verify your email address before you can add other users to this account. Check your email for the verification link.',
      glyph: ERROR_GLYPHS.envelope,
      blockedReason: 'Verify your email address to continue.',
      // No resend action: the only resend endpoint is an admin acting on another user, so
      // there is nothing to offer someone whose own address is unverified. The link they
      // need is already in their inbox, which is what the detail says.
      actions: [{ id: 'recheck-verification', label: "I've Verified — Recheck", kind: 'quiet' }],
      persistence: 'until-state-change',
    })
  }

  if (lower.includes('you must wait at least') && lower.includes('seconds')) {
    const match = raw.match(/(\d+(?:\.\d+)?)\s*seconds/)
    const waitSeconds = match ? Math.ceil(parseFloat(match[1])) : 120
    return build({
      severity: 'gating',
      tier: 'surface',
      title: `Wait ${waitSeconds} seconds before resending`,
      detail: `You must wait at least ${waitSeconds} seconds since the last email was sent. Check your spam folder while you wait.`,
      glyph: ERROR_GLYPHS.clock,
      blockedReason: 'Wait for the timer before resending.',
      // The wait is on the control: it counts down on the button and the button goes live at
      // zero, so the user resends from the same place rather than starting over.
      actions: [{ id: 'resend-verification', label: 'Resend Verification', countdownSeconds: waitSeconds }],
      persistence: { ms: waitSeconds * 1000 },
    })
  }

  // Per-application restore rejections. The backend joins these into one string; the
  // structure is recovered by parseApplicationErrors and rendered as rows.
  if (/^Application .+ cannot be restored/m.test(raw)) {
    return presentRestoreRejection(raw, build)
  }

  if (lower.includes('restore is disabled for application')) {
    return build({
      severity: 'failure',
      tier: 'object',
      title: 'Recovery is turned off for this application',
      detail:
        "Backups continue, but tests and recoveries are blocked until recovery is re-enabled in this application's configuration.",
      glyph: ERROR_GLYPHS.disabled,
      actions: [COPY_DETAILS],
      persistence: 'until-state-change',
    })
  }

  if (lower.includes('only completed jobs can be retried')) {
    return build({
      severity: 'failure',
      tier: 'object',
      title: 'This job is still running',
      detail:
        'Only completed jobs can be retried — the list was out of date when you clicked. Refresh to see its current status.',
      actions: [COPY_DETAILS],
      persistence: 'until-state-change',
    })
  }

  if (lower.includes('resume recovery is not available')) {
    return build({
      severity: 'failure',
      tier: 'object',
      title: "This recovery can't be resumed",
      detail: (causeCode && CAUSE_CODE_COPY[causeCode]) || 'Start a new recovery from a current recovery point.',
      actions: [COPY_DETAILS],
      persistence: 'until-state-change',
    })
  }

  // Recovery point locks surface as a 422 wrapping the lock message, e.g. "Restore request
  // failed because of the following issue: Lock for Recovery Point {id} is already held. Try
  // again later." Nothing retries on its own, so the copy asks the user to try again.
  if (lower.includes('recovery point') && lower.includes('is already held')) {
    return build({
      severity: 'gating',
      tier: 'object',
      title: 'This recovery point is in use',
      detail:
        'Another operation holds this recovery point, so your recovery could not start. Wait for that to finish, then start the recovery again.',
      glyph: ERROR_GLYPHS.lock,
      actions: [COPY_DETAILS],
      persistence: 'until-state-change',
    })
  }

  if (lower.includes('is not entitled to restore')) {
    return build({
      severity: 'failure',
      tier: 'page',
      title: "This account can't run recoveries",
      detail: 'Backups and recovery points continue as normal. Talk to your account team to enable recovery.',
      actions: [{ id: 'contact-account-team', label: 'Contact Account Team', href: SUPPORT_CONTACT_URL }],
      persistence: 'until-state-change',
    })
  }

  if (lower.includes('no applications to restore')) {
    // An empty state, not a failure.
    return build({
      severity: 'info',
      tier: 'page',
      title: 'No applications to restore',
      detail: 'Recovery needs at least one protected application with a recovery point.',
      glyph: ERROR_GLYPHS.info,
      actions: [{ id: 'create-application', label: 'Create an Application »' }],
      persistence: 'until-state-change',
    })
  }

  if (lower.includes('unable to find hubspot chat widget')) {
    return build({
      severity: 'neutral',
      tier: 'global',
      title: 'Chat is unavailable',
      detail: 'Email support@arpio.io and we’ll pick it up from there.',
      glyph: ERROR_GLYPHS.chatOff,
      actions: [{ id: 'email-support', label: 'Email Support', href: SUPPORT_EMAIL_URL }],
      persistence: { ms: 6000 },
    })
  }

  return null
}

/**
 * Restore rejections arrive as one string per application, joined with a blank line, each
 * optionally followed by two-space-indented issue lines. See arpio-api restore_api.py.
 */
function presentRestoreRejection(raw: string, build: Build): PresentedError {
  const items = parseApplicationErrors(raw)
  const appIds = items.filter((item) => item.kind === 'application' && item.id).map((item) => item.id)
  const distinctApps = [...new Set(appIds)]

  if (distinctApps.length > 1) {
    // One row per application, carrying that application's own issue text. Passing the parsed
    // items straight through put the backend's header line in the row (repeating the app id
    // it already shows) and rendered every issue a second time as an unattributed bullet list.
    const rows: PresentedErrorItem[] = distinctApps.map((id) => {
      const reasons = items.filter((item) => item.kind === 'issue' && item.id === id).map((item) => item.reason)
      return {
        id,
        kind: 'application',
        reason: reasons.length ? reasons.join(' ') : 'The request was rejected.',
      }
    })

    return build({
      severity: 'failure',
      tier: 'surface',
      title: `${distinctApps.length} applications can't be recovered`,
      detail: 'Nothing was started, and production was not modified.',
      blockedReason: 'Resolve the issues above to continue.',
      actions: [COPY_DETAILS],
      items: rows,
      persistence: 'until-closed',
    })
  }

  const appId = appIds[0]
  const issues = items.filter((item) => item.kind === 'issue')

  if (issues.length === 0) {
    return build({
      severity: 'failure',
      tier: 'surface',
      title: 'This recovery request was rejected',
      detail: `Arpio couldn't process the request${
        appId ? ` for ${appId}` : ''
      }. Nothing was started and production is untouched. Reload and try once more — if it repeats, send the reference below to support.`,
      actions: [{ id: 'reload-retry', label: 'Reload and Retry' }, COPY_DETAILS, CONTACT_SUPPORT],
      persistence: 'until-closed',
    })
  }

  return build({
    severity: 'failure',
    tier: 'surface',
    title: appId ? `${appId} can't be recovered yet` : "This application can't be recovered yet",
    detail: `${issues.length} ${issues.length === 1 ? 'issue is' : 'issues are'} blocking this recovery:`,
    blockedReason: 'Resolve the issue above to continue.',
    actions: [COPY_DETAILS],
    items: issues,
    persistence: 'until-closed',
  })
}

/**
 * Recovers the structure the backend flattened. Exported so the parser can be tested and
 * deleted once arpio-api returns `items[]` and a request id.
 */
export function parseApplicationErrors(raw: string): PresentedErrorItem[] {
  const items: PresentedErrorItem[] = []

  for (const block of raw.split('\n\n')) {
    const lines = block.split('\n')
    const header = lines[0]?.trim()
    if (!header) continue

    const appMatch = header.match(/^Application (\S+) cannot be restored/)
    items.push({
      id: appMatch ? appMatch[1] : undefined,
      kind: 'application',
      reason: header,
    })

    for (const line of lines.slice(1)) {
      // The backend prefixes each issue with exactly two spaces.
      if (!line.startsWith('  ')) continue
      const reason = line.trim()
      if (reason) items.push({ id: appMatch ? appMatch[1] : undefined, kind: 'issue', reason })
    }
  }

  return items
}

/**
 * The tier follows the surface the call site named, innermost first: a submit from a dialog
 * belongs in that dialog, and the same failure raised from a panel belongs on the object.
 * `unnamedAnchor` covers a call site that only reports a surface is mounted without saying
 * which, which is what a preview supplies.
 */
function anchoredTier(context: ErrorContext, unnamedAnchor: ErrorTier): ErrorTier {
  if (context.dialogId) return 'surface'
  if (context.objectId) return 'object'
  return context.anchored ? unnamedAnchor : 'global'
}

function byResponseCode(code: number, raw: string | undefined, context: ErrorContext, build: Build): PresentedError {
  // A generic action failure belongs to the surface that submitted it. Naming an object does
  // not make a 500 a fact about that object, so it falls through to a toast rather than pinning
  // to the panel — object tier is decided by the message matchers above, never by a code.
  const submittedFromDialog = context.dialogId !== undefined || (context.anchored && context.objectId === undefined)
  const actionTier: ErrorTier = submittedFromDialog ? 'surface' : 'global'

  switch (code) {
    case 0:
      return build({
        severity: 'gating',
        tier: 'global',
        title: 'Unable to reach the server',
        detail: "Reconnecting — data on this page may be out of date. Nothing you've started was lost.",
        glyph: ERROR_GLYPHS.connectivity,
        actions: [{ id: 'retry-now', label: 'Retry Now' }],
        persistence: 'self-clearing',
      })

    case 400:
      return build({
        severity: 'failure',
        tier: actionTier,
        title: 'This request was rejected',
        detail: raw
          ? "Arpio couldn't process the request. Nothing was changed. Reload and try once more — if it repeats, send the reference below to support."
          : 'Check your input and try again.',
        // No remedy is knowable without the backend detail, but never leave a dead end.
        actions: raw
          ? [{ id: 'reload-retry', label: 'Reload and Retry' }, COPY_DETAILS, CONTACT_SUPPORT]
          : [COPY_DETAILS, CONTACT_SUPPORT],
        persistence: raw ? 'sticky' : 'until-closed',
      })

    case 401:
      return build({
        severity: 'neutral',
        tier: 'global',
        title: 'Authentication required',
        detail:
          'Please sign in to continue. Nothing in progress was lost — running recoveries and backups are unaffected.',
        glyph: ERROR_GLYPHS.session,
        actions: [{ id: 'sign-in', label: 'Sign In Again' }],
        persistence: 'sticky',
      })

    case 403:
      return build({
        severity: 'failure',
        // A refusal raised from a dialog belongs in that dialog. Everything else is an
        // account-scope gate the user did not trigger, which belongs at the top of the page —
        // never a toast, so this does not fall through to `global`.
        tier: context.dialogId ? 'surface' : 'page',
        title: 'Access denied',
        // The backend names the actual reason — the last identity provider, one still in use —
        // and that sentence is what the user can act on. The generic line is the fallback.
        detail: raw || 'You do not have permission to perform this action. Ask an account admin for the role you need.',
        glyph: ERROR_GLYPHS.lock,
        actions: [COPY_DETAILS],
        persistence: 'until-state-change',
      })

    case 404:
      // Nothing failed — the object simply isn't there.
      return build({
        severity: 'neutral',
        tier: 'page',
        title: 'Not found',
        detail:
          'The requested resource could not be found. It may have been deleted, or you may not have access to it in this account.',
        glyph: ERROR_GLYPHS.question,
        actions: [{ id: 'back-to-applications', label: 'Back to Applications' }],
        persistence: 'until-state-change',
      })

    case 409: {
      // A duplicate or a busy resource belongs to the surface that submitted it, so it follows
      // the named surface. A stale view does not: the remedy is to reload the data behind the
      // dialog, which the dialog cannot do, so it stays with the object or falls through.
      const conflictTier: ErrorTier = anchoredTier(context, 'object')
      const staleViewTier: ErrorTier = context.anchored ? 'object' : 'global'
      // A 409 that names its own reason is a duplicate or a busy resource, so Refresh is a dead
      // end and "your view is out of date" contradicts the message the user can already read.
      if (raw && !raw.toLowerCase().includes(STOCK_CONFLICT_DESCRIPTION)) {
        return build({
          severity: 'failure',
          tier: conflictTier,
          title: DUPLICATE_CONFLICT.test(raw) ? 'That already exists' : 'This request conflicts with the current state',
          detail: raw,
          actions: [COPY_DETAILS],
          persistence: 'until-closed',
        })
      }
      return build({
        severity: 'failure',
        tier: staleViewTier,
        title: 'Your view is out of date',
        detail: 'This action conflicts with the current state. Refresh to see what changed, then try again.',
        actions: [{ id: 'refresh', label: 'Refresh' }],
        persistence: 'until-state-change',
      })
    }

    case 422:
      return build({
        severity: 'failure',
        tier: actionTier,
        title: raw ? 'This request was rejected' : 'Check your input',
        // A 422 means the request was understood and refused for a reason the backend named,
        // and that sentence is the remedy — so it leads, rather than sitting in the reference.
        detail: raw ?? 'Please check your input and try again.',
        // No support handoff: the reason is on screen and resolving it belongs to the user.
        // Copy Details still carries the reference if they do need to escalate.
        actions: [COPY_DETAILS],
        persistence: raw ? 'sticky' : 'until-closed',
      })

    case 500:
      return build({
        severity: 'failure',
        tier: actionTier,
        title: 'Arpio hit an internal error',
        detail:
          'Nothing you started was lost. Try again in a moment — if it repeats, send the reference below to support.',
        actions: [COPY_DETAILS, CONTACT_SUPPORT],
        persistence: 'sticky',
      })

    default:
      return build({
        severity: 'failure',
        tier: actionTier,
        title: 'Something went wrong',
        detail:
          'An unexpected error occurred. Try again in a moment — if it repeats, send the reference below to support.',
        actions: [COPY_DETAILS, CONTACT_SUPPORT],
        persistence: 'sticky',
      })
  }
}

export function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}
